import WorkOrderStatusPill from '@atom/components/common/workOrderDetail/workOrderStatusPill/WorkOrderStatusPill';
import { useNavigation } from '@atom/hooks/useNavigation';
import { ListTable, Progress } from '@atom/mui';
import { AssetUsage } from '@atom/types/asset';
import { ConversionUnit } from '@atom/types/schema';
import { numberToLocaleString } from '@atom/utilities/currencyUtility';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

import {
  formatQuantityWithUnit,
  toConvertedQuantity,
} from './costTabUtilities';

import classes from './costTab.module.css';

export const RESULTS_PER_PAGE_OPTIONS = [25, 50, 100];

const {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
} = ListTable;

const styles = {
  tableContainer: {
    height: '100%',
  },
  link: {
    fontWeight: '500',
  },
  statusPill: {
    margin: 0,
  },
};

const USAGE_COLUMN = 'Usage';
const COST_COLUMN = 'Cost';

const RIGHT_ALIGNED_COLUMNS = [USAGE_COLUMN, COST_COLUMN];

const COLUMNS = [
  'Work ID',
  'Status',
  'Due Date',
  'Completion Date',
  USAGE_COLUMN,
  COST_COLUMN,
];

export type UsageTableProps = {
  usage: AssetUsage[];
  totalCount: number;
  usageLoading: boolean;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  unit?: string;
  conversionUnit?: ConversionUnit;
};

const UsageTable = ({
  usage,
  totalCount,
  usageLoading,
  page,
  limit,
  onPageChange,
  onLimitChange,
  unit,
  conversionUnit,
}: UsageTableProps) => {
  const [, { Link }] = useNavigation();

  if (usageLoading) {
    return <Progress />;
  }

  return (
    <ListTable TableContainerProps={{ style: styles.tableContainer }}>
      <TableHead>
        <TableRow header>
          {COLUMNS.map(label => (
            <TableCell
              key={label}
              variant="head"
              align={
                RIGHT_ALIGNED_COLUMNS.includes(label) ? 'right' : undefined
              }
            >
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {usage.map(usageItem => (
          <TableRow
            key={usageItem.id}
            height="3rem"
            data-cy={`usageTableRow-${usageItem.name}`}
          >
            <TableCell>
              <Link
                style={styles.link}
                to={`/workOrders/${usageItem.id}`}
                data-cy={`usageTableLink-${usageItem.name}`}
              >
                {usageItem.name}
              </Link>
            </TableCell>
            <TableCell>
              <WorkOrderStatusPill
                className="table"
                statusId={usageItem.statusId}
                style={styles.statusPill}
              />
            </TableCell>
            <TableCell>{setDisplayDate(usageItem.dueDate)}</TableCell>
            <TableCell>{setDisplayDate(usageItem.completionDate)}</TableCell>
            {/* right-aligned cells lay children out in a row, so the lines share one wrapper */}
            <TableCell align="right">
              <div className={classes.usageCell}>
                <div className={classes.usageUnit}>
                  {formatQuantityWithUnit(usageItem.totalUsage, unit)}
                </div>
                {conversionUnit && (
                  <div className={classes.conversionSubtext}>
                    {formatQuantityWithUnit(
                      toConvertedQuantity(usageItem.totalUsage, conversionUnit),
                      conversionUnit.unit,
                    )}
                  </div>
                )}
              </div>
            </TableCell>
            <TableCell align="right">
              {numberToLocaleString(usageItem.totalCost)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            rowsPerPageOptions={RESULTS_PER_PAGE_OPTIONS}
            count={totalCount}
            rowsPerPage={limit}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={event => onLimitChange(+event.target.value)}
          />
        </TableRow>
      </TableFooter>
    </ListTable>
  );
};

export default UsageTable;
